import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';

function Portfolio() {
  const [portfolio, setPortfolio] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', display_order: '' });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    const response = await axios.get(`${API_URL}/portfolio/admin`);
    setPortfolio(response.data);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check file size on client side before uploading
    if (selectedImage && selectedImage.size > 5 * 1024 * 1024) {
      alert('Image file is too large. Maximum size is 5MB. Please choose a smaller image.');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('display_order', formData.display_order || 0);

    if (editingItem) {
      formDataToSend.append('active', editingItem.active);
      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }
      await axios.put(`${API_URL}/portfolio/${editingItem.id}`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } else {
      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      } else {
        alert('Please select an image');
        return;
      }
      await axios.post(`${API_URL}/portfolio`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }

    setShowModal(false);
    setEditingItem(null);
    setFormData({ title: '', description: '', display_order: '' });
    setSelectedImage(null);
    setImagePreview(null);
    fetchPortfolio();
  };

  const deleteItem = async (id) => {
    if (window.confirm('Delete this portfolio item? This will also delete the image file.')) {
      await axios.delete(`${API_URL}/portfolio/${id}`);
      fetchPortfolio();
    }
  };

  const toggleActive = async (item) => {
    const formDataToSend = new FormData();
    formDataToSend.append('title', item.title);
    formDataToSend.append('description', item.description);
    formDataToSend.append('display_order', item.display_order);
    formDataToSend.append('active', item.active === 1 ? 0 : 1);

    await axios.put(`${API_URL}/portfolio/${item.id}`, formDataToSend, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    fetchPortfolio();
  };

  return (
    <div className="portfolio-admin">
      <div className="header">
        <h2>Portfolio Management</h2>
        <button onClick={() => setShowModal(true)}>Add Portfolio Item</button>
      </div>

      <div className="portfolio-grid">
        {portfolio.map(item => (
          <div key={item.id} className={`portfolio-card ${item.active === 0 ? 'inactive' : ''}`}>
            <div className="portfolio-image-container">
              <img src={item.image_url} alt={item.title} />
              {item.active === 0 && <div className="inactive-overlay">Hidden</div>}
            </div>
            <div className="portfolio-card-info">
              <h3>{item.title}</h3>
              {item.description && <p>{item.description}</p>}
              <p className="display-order">Order: {item.display_order}</p>
              <div className="portfolio-card-actions">
                <button onClick={() => toggleActive(item)} className="btn-small">
                  {item.active === 1 ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => {
                    setEditingItem(item);
                    setFormData({ title: item.title, description: item.description, display_order: item.display_order });
                    setImagePreview(item.image_url);
                    setShowModal(true);
                  }}
                  className="btn-small"
                >
                  Edit
                </button>
                <button onClick={() => deleteItem(item.id)} className="btn-small delete">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {portfolio.length === 0 && (
        <p className="no-results">No portfolio items yet. Add your first one to get started!</p>
      )}

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>{editingItem ? 'Edit Portfolio Item' : 'Add Portfolio Item'}</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
              <textarea
                placeholder="Description (optional)"
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="3"
              />
              <input
                type="number"
                placeholder="Display Order (0 = first)"
                value={formData.display_order}
                onChange={(e) => setFormData({...formData, display_order: e.target.value})}
              />

              <div className="form-group">
                <label htmlFor="portfolio-image-upload">Portfolio Image {!editingItem && '(Required)'}</label>
                <input
                  id="portfolio-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                />
                {selectedImage && (
                  <p className="file-info" style={{
                    color: selectedImage.size > 5 * 1024 * 1024 ? 'var(--danger)' : 'var(--text-light)',
                    fontSize: '0.85rem',
                    marginTop: '5px'
                  }}>
                    File size: {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                    {selectedImage.size > 5 * 1024 * 1024 && ' (Too large!)'}
                  </p>
                )}
              </div>

              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <p className="image-label">{editingItem && !selectedImage ? 'Current Image' : 'Preview'}</p>
                </div>
              )}

              <div className="modal-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => {
                  setShowModal(false);
                  setEditingItem(null);
                  setFormData({ title: '', description: '', display_order: '' });
                  setSelectedImage(null);
                  setImagePreview(null);
                }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}



export default Portfolio;
